import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import api from '../../services/api';
import CustomModal from '../../components/Modals/CustomModal';
import JustificativaVacinaForm from '../../components/Forms/JustificativaVacinaForm';
import { useToast } from '../../hooks/useToast';
import ToastManager from '../../components/Toast/ToastManager';
import {
  PageContainer,
  PageHeader,
  BreadcrumbNav,
  BreadcrumbLink,
  PageTitle,
  GestacaoSelectorContainer,
  TableContainer,
  VacinasTable,
  VacinaNome,
  DosesList,
  DoseBadge,
  SituacaoBadge,
  JustificativaText,
  PaginationContainer,
  PaginationButton,
  PageInfo,
  EditIcon,
  LoadingContainer,
  ErrorContainer,
} from './HistoricoVacinaPage.styles';

const HistoricoVacinaPage = () => {
  const { cpf } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [gestacoes, setGestacoes] = useState([]);
  const [gestacaoSelecionada, setGestacaoSelecionada] = useState(null);
  const [gestanteHabilitada, setGestanteHabilitada] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVacina, setSelectedVacina] = useState(null);
  const { toasts, removeToast, showSuccess, showError } = useToast();

  useEffect(() => {
    fetchGestacoes();
    fetchGestanteStatus();
  }, [cpf]);

  useEffect(() => {
    if (gestacaoSelecionada) {
      fetchHistoricoVacina();
    }
  }, [gestacaoSelecionada]);

  const fetchGestacoes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/gestante/${cpf}/consultas`);
      const gestacoesList = response.data;
      setGestacoes(gestacoesList);

      const emAndamento = gestacoesList.find(
        g => g.status_gravidez === "Em Andamento"
      );

      if (emAndamento) {
        setGestacaoSelecionada(emAndamento);
      } else if (gestacoesList.length > 0) {
        setGestacaoSelecionada(gestacoesList[0]);
      } else {
        setError('Nenhuma gestação encontrada para esta gestante.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erro ao buscar gestações:', err);
      if (err.response?.status === 404) {
        setError('Gestante não encontrada.');
      } else if (err.response?.status === 401) {
        setError('Usuário não autenticado.');
      } else {
        setError('Erro ao carregar as gestações.');
      }
      setLoading(false);
    }
  };

  const fetchGestanteStatus = async () => {
    try {
      const response = await api.get(`/gestante/${cpf}/list`);
      setGestanteHabilitada(response.data?.habilitado !== false);
    } catch (err) {
      console.error('Erro ao buscar status da gestante:', err);
    }
  };

  const fetchHistoricoVacina = async () => {
    if (!gestacaoSelecionada?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/vacinas/historico/${gestacaoSelecionada.id}`);
      const historicoData = response.data;

      if (!historicoData) {
        setError('Nenhum histórico de vacinação encontrado para esta gestação.');
        setData(null);
      } else {
        setData(historicoData);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Erro ao buscar histórico de vacinação:', err);
      if (err.response?.status === 404) {
        setError('Histórico de vacinação não encontrado para esta gestação.');
      } else if (err.response?.status === 401) {
        setError('Usuário não autenticado.');
      } else {
        setError('Erro ao carregar o histórico de vacinação.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  const handleEditarVacina = (vacina) => {
    if (!gestanteHabilitada) return;
    setSelectedVacina(vacina);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVacina(null);
  };

  const handleSuccess = (message) => {
    showSuccess(message);
    fetchHistoricoVacina();
  };

  const handleGestacaoChange = (e) => {
    const gestacao = gestacoes.find(g => String(g.id) === e.target.value);
    if (gestacao) {
      setData(null);
      setGestacaoSelecionada(gestacao);
    }
  };

  const gestacaoSelector = gestacoes.length > 0 && (
    <GestacaoSelectorContainer>
      <label htmlFor="gestacao-selector">Gestação:</label>
      <select
        id="gestacao-selector"
        value={gestacaoSelecionada?.id || ''}
        onChange={handleGestacaoChange}
      >
        {gestacoes.map((g) => (
          <option key={g.id} value={g.id}>
            {g.data} - {g.status_gravidez}
          </option>
        ))}
      </select>
    </GestacaoSelectorContainer>
  );

  const parseDate = (value) => {
    if (!value) return null;

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return new Date(year, parseInt(month) - 1, day);
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>Carregando histórico de vacinação...</LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader>
          <BreadcrumbNav>
            <BreadcrumbLink onClick={handleVoltar}>← Voltar</BreadcrumbLink>
          </BreadcrumbNav>
          <PageTitle>Histórico de Vacinações</PageTitle>
        </PageHeader>
        {gestacaoSelector}
        <ErrorContainer>{error}</ErrorContainer>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer>
        <PageHeader>
          <BreadcrumbNav>
            <BreadcrumbLink onClick={handleVoltar}>← Voltar</BreadcrumbLink>
          </BreadcrumbNav>
          <PageTitle>Histórico de Vacinações</PageTitle>
        </PageHeader>
        {gestacaoSelector}
        <ErrorContainer>Nenhum dado disponível</ErrorContainer>
      </PageContainer>
    );
  }

  const statusVacinas = (data.status_vacinas || [])
    .slice()
    .sort((a, b) => a.tipo_vacina.id - b.tipo_vacina.id);

  const totalPages = Math.ceil(statusVacinas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const vacinasPaginadas = statusVacinas.slice(startIndex, endIndex);

  return (
    <PageContainer>
      <PageHeader>
        <BreadcrumbNav>
          <BreadcrumbLink onClick={handleVoltar}>Home</BreadcrumbLink>
          {' / '}
          <BreadcrumbLink onClick={handleVoltar}>Gestantes</BreadcrumbLink>
          {' / '}
          <span>Histórico de Vacinações</span>
        </BreadcrumbNav>
        <PageTitle>Histórico de Vacinações</PageTitle>
      </PageHeader>

      {gestacaoSelector}

      <TableContainer>
        <VacinasTable>
          <thead>
            <tr>
              <th style={{ width: '15%' }}>Vacina</th>
              <th style={{ width: '40%' }}>Doses Tomadas</th>
              <th style={{ width: '15%' }}>Situação</th>
              <th style={{ width: '25%' }}>Justificativa</th>
              <th style={{ width: '5%', textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {vacinasPaginadas.map((vacina) => {
              const dosesOrdered = [...vacina.doses].sort(
                (a, b) => new Date(a.data_vacinacao) - new Date(b.data_vacinacao)
              );

              return (
                <tr key={vacina.tipo_vacina.id}>
                  <td>
                    <VacinaNome>{vacina.tipo_vacina.nome}</VacinaNome>
                  </td>
                  <td>
                    <DosesList>
                      {dosesOrdered.map((dose, doseIdx) => (
                        <DoseBadge key={doseIdx} $status="tomada">
                          {doseIdx + 1}ª Dose - {parseDate(dose.data_vacinacao)?.toLocaleDateString('pt-BR')}
                        </DoseBadge>
                      ))}
                      {vacina.doses.length === 0 && !vacina.justificativa && (
                        <span style={{ fontSize: '12px', color: '#999' }}>-</span>
                      )}
                    </DosesList>
                  </td>
                  <td>
                    <SituacaoBadge $status={vacina.situacao_status}>
                      {vacina.situacao_label}
                    </SituacaoBadge>
                  </td>
                  <td>
                    {vacina.justificativa ? (
                      <JustificativaText>
                        {vacina.justificativa.tipo_justificativa.justificativa}
                      </JustificativaText>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#999' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {vacina.situacao_status === 'incompleta' && (
                      <EditIcon
                        disabled={!gestanteHabilitada}
                        onClick={() => handleEditarVacina(vacina)}
                        title={gestanteHabilitada ? 'Editar vacinação' : 'Gestante desabilitada'}
                      >
                        <MdEdit />
                      </EditIcon>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </VacinasTable>
      </TableContainer>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            ← Anterior
          </PaginationButton>
          <PageInfo>
            Página {currentPage} de {totalPages}
          </PageInfo>
          <PaginationButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
            Próxima →
          </PaginationButton>
        </PaginationContainer>
      )}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Informar justificativa"
      >
        {selectedVacina && gestacaoSelecionada && (
          <JustificativaVacinaForm
            gestanteCpf={cpf}
            gravidezId={gestacaoSelecionada.id}
            tipoVacina={selectedVacina.tipo_vacina}
            preselectedJustificativaId={selectedVacina.justificativa?.tipo_justificativa?.id || null}
            onClose={handleCloseModal}
            onSuccess={handleSuccess}
          />
        )}
      </CustomModal>

      <ToastManager toasts={toasts} removeToast={removeToast} />
    </PageContainer>
  );
};

export default HistoricoVacinaPage;
